import React, { useMemo } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { DNABox, DNACard, DNAText, Iffy, DNAButton } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';
import {
  CommonIcon,
  DNAChip,
  DNACustomFieldChipList,
  DNAFileTypeChip,
  DNAThumbnail,
  DNAPopover,
} from 'src/components/DNA';
import { detectArchivedFileKeyPath } from 'src/components/SlideSelector/useThumbnailSelector';
import { LoadedPresentation, PresentableModelORM } from 'src/state/context/ContentProvider/ContentProvider';
import { isDocumentVersionORM, isFolderItemORM, isPageGroupORM } from 'src/types/typeguards';
import { OpenedPresentationsVariantOption } from './OpenedPresentations';
import DocumentsAssociated from './DocumentsAssociated';
import { useMeetingsState } from 'src/state/context/Meetings/MeetingsStateProvider.proxy';
import { getPresentationVirtualStatus } from 'src/screens/Meetings/util';

const styles = StyleSheet.create({
  containerStyle: {
    paddingLeft: 8,
    paddingRight: 12,
    paddingTop: 8,
    backgroundColor: colors['color-ui2-neutral-700'],
    marginVertical: 4,
    borderRadius: 4,
  },
  inPersonControlContainerStyle: {
    paddingLeft: 8,
    paddingRight: 12,
    paddingVertical: 8,
    paddingBottom: 8,
    backgroundColor: 'transparent',
    marginVertical: 4,
    borderBottomColor: 'rgba(255, 255, 255, 0.16)',
    borderBottomWidth: 1,
  },
  activeItem: {
    backgroundColor: 'rgba(48, 173, 127, 0.16)',
    borderWidth: 1,
    borderColor: colors['color-success-500'],
    bordeRadius: 4,
    paddingLeft: 8,
    paddingRight: 12,
    paddingTop: 8,
    marginVertical: 4,
  },
});

export interface OpenedPresentationRowProps {
  isVirtual?: boolean
  item: LoadedPresentation,
  removePresentation: (id: string) => void,
  changeActivePresentation: (id: string) => void,
  activePresentation?: LoadedPresentation,
  variant?:OpenedPresentationsVariantOption,
  associatedDocuments?:LoadedPresentation[],
  isAssociatedDocument?: boolean,
  onCloseActionBarPopout?: () => void,
}

enum Actions {
  change,
  close
}
type Action = keyof typeof Actions
type ActionEffects = Record<Action, (id: string, optionalCondition?: (orm?: PresentableModelORM) => boolean) => void>

const DocumentOpenedPresentationRow: React.FC<OpenedPresentationRowProps> = ({
  item,
  removePresentation,
  changeActivePresentation,
  activePresentation,
  variant = 'default',
  associatedDocuments,
  isAssociatedDocument,
  isVirtual,
  onCloseActionBarPopout,
}) => {
  const { setPresentationControlsVisible, setCurrentTab, setSelectedPresentableId } = useMeetingsState()
  const s3URL = detectArchivedFileKeyPath(item.currentPresentablePage.documentVersionORM?.model,
    item.currentPresentablePage.page)
  const isActive = activePresentation?.presentable.id === item.presentable.id;
  const isDocumentVersion = !isPageGroupORM(item.presentable.orm) && (isDocumentVersionORM(item.presentable.orm) ||
  isDocumentVersionORM(item.presentable.orm.relations.itemORM));
  const isModifiedDocumentVersion = isFolderItemORM(item.presentable.orm) && isDocumentVersion &&
  item.presentable.orm.model.visiblePages?.length;
  const presentationVirtualStatus =
    useMemo(() => getPresentationVirtualStatus(item.presentable.orm), [item.presentable.orm]);
  const isContentDisabled = isVirtual && presentationVirtualStatus.isHTMLWebPresentation;
  const isFromActionBar = variant === 'actionBar'

  const rowStyle = [
    !isFromActionBar && isActive && styles.activeItem,
    !isFromActionBar && !isActive && styles.containerStyle,
    isFromActionBar && styles.inPersonControlContainerStyle,
  ]

  const actionEffects: ActionEffects = {
    change: changeActivePresentation,
    close: removePresentation,
  }

  const canOpenPresentation = (orm?: PresentableModelORM): boolean => {
    return !getPresentationVirtualStatus(orm)?.isHTMLWebPresentation
  };

  const triggerAction = (action:Action) => {
    actionEffects[action](item.presentable.id, canOpenPresentation)
  }

  return (
    <DNAPopover disablePopover={!isContentDisabled} placement="top-start">
      <DNAPopover.Anchor style={{ flex: 1 }}>
        <DNACard
          appearance="flat"
          style={rowStyle}
        >
          <DNABox
            alignY="center"
            appearance="col"
            disabled={isContentDisabled}
            spacing="sm"
            childFill={0}
          >
            <DNABox spacing="between" alignY="center" childFill={0}>
              <Pressable
                onPress={() => triggerAction('change')}
                style={isAssociatedDocument && { paddingLeft: 40 }}
              >
                <DNABox spacing="md" alignY="center" childFill={1}>
                  <DNABox
                    style={{
                      backgroundColor: colors['color-gray-500'],
                      borderWidth: 1,
                      borderColor: colors['color-text-tertiary'],
                    }}
                  >
                    <DNAThumbnail
                      s3URL={s3URL}
                      useLoadingIndicator={true}
                      width={80}
                      height={46}
                      variant={DNAThumbnail.Variants.MEETING}
                    />
                  </DNABox>
                  <DNABox spacing="sm" fill alignY="center" childFill={0}>

                    {/* Content Info */}
                    <DNABox spacing="sm" appearance="col" fill>
                      <DNABox spacing="xs" appearance="col" fill>
                        <DNAText testID="document-presenation-title" numberOfLines={2} colorMode="dark">
                          {item.presentable.title}
                        </DNAText>
                        { isPageGroupORM(item.presentable.orm) &&
                          <DNAText numberOfLines={2} status="subtle">{item.presentable.orm.model.name}</DNAText>
                          }
                      </DNABox>
                      <DNABox spacing="sm">
                        <Iffy is={isActive && variant !== 'actionBar'}>
                          <DNAChip.Beacon appearance="filled" status="success" children="PRESENTING..." />
                        </Iffy>
                        <Iffy is={isDocumentVersion}>
                          <DNABox spacing="sm">
                            <DNACustomFieldChipList
                              documentVersionORM={item.currentPresentablePage.documentVersionORM}
                            />
                            <DNAFileTypeChip orm={item.currentPresentablePage.documentVersionORM} />
                          </DNABox>
                        </Iffy>
                        <Iffy is={isModifiedDocumentVersion || !isDocumentVersion}>
                          <DNAChip.Beacon variant="MODIFIED" />
                        </Iffy>
                        <Iffy is={isContentDisabled}>
                          <DNAChip.Beacon
                            testID={`content-not-available-${item.presentable.orm.model.id}`}
                            variant="CONTENT_UNAVAILABLE"
                          />
                        </Iffy>
                        <Iffy is={isAssociatedDocument}>
                          <DNAChip.Beacon variant="DOCUMENT_ASSOCIATED_FILE" />
                        </Iffy>
                      </DNABox>
                    </DNABox>

                    {/* Action buttons */}
                    <Iffy is={!isFromActionBar && !isContentDisabled}>
                      <DNABox spacing="sm" fill alignY="center">
                        <DNAButton
                          appearance="ghost"
                          status="subtle"
                          onPress={() => triggerAction('close')}
                          colorMode="dark"
                          children="Close"
                        />
                      </DNABox>
                    </Iffy>
                    <Iffy is={isFromActionBar && isActive}>
                      <DNABox fill alignY="center">
                        <CommonIcon variant="check" />
                      </DNABox>
                    </Iffy>
                    <Iffy is={isFromActionBar}>
                      <DNAButton
                        appearance="outline"
                        status="subtle"
                        iconLeft="x"
                        colorMode="dark"
                        onPress={() => triggerAction('close')}
                      />
                    </Iffy>
                  </DNABox>
                </DNABox>
              </Pressable>
            </DNABox>
            <Iffy is={associatedDocuments?.length && !isFromActionBar}>
              <DocumentsAssociated
                associatedDocuments={associatedDocuments}
                isActive={isActive}
                parentPresentableId={item.presentable.id}
                handleShowPresentation={changeActivePresentation}
              />
            </Iffy>
            <Iffy is={isFromActionBar && associatedDocuments?.length}>
              <DNABox fill alignY="center">
                <DNAButton
                  testID="view-associated-files-btn"
                  appearance="outline"
                  status="subtle"
                  colorMode="dark"
                  onPress={() => {
                    setPresentationControlsVisible(true)
                    setCurrentTab('OPENED_PRESENTATIONS')
                    setSelectedPresentableId(item.presentable.id)
                    onCloseActionBarPopout?.()
                  }}
                  children="View associated files"
                />
              </DNABox>
            </Iffy>
          </DNABox>
        </DNACard>
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText testID="web-html-custom-popup" colorMode="dark">
          {presentationVirtualStatus.message}
        </DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  )
}

export default DocumentOpenedPresentationRow
